<script setup lang="ts">
import { SUPPORTED_LANGUAGES, type SurfaceLanguage } from './languages'
import { useSurfaceLocale } from './useSurfaceLocale'

defineProps<{ label: string; labelledBy?: string; labels: Readonly<Record<SurfaceLanguage, string>>; accessibleLabels: Readonly<Record<SurfaceLanguage, string>>; surface?: 'console' | 'cosmos' }>()
const { language, setLanguage } = useSurfaceLocale()

function navigate(event: KeyboardEvent, current: SurfaceLanguage): void {
  const index = SUPPORTED_LANGUAGES.indexOf(current)
  const last = SUPPORTED_LANGUAGES.length - 1
  const target = event.key === 'Home' ? 0 : event.key === 'End' ? last
    : ['ArrowRight', 'ArrowDown'].includes(event.key) ? (index + 1) % SUPPORTED_LANGUAGES.length
      : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? (index + last) % SUPPORTED_LANGUAGES.length : undefined
  if (target === undefined) return
  event.preventDefault()
  void setLanguage(SUPPORTED_LANGUAGES[target]!)
  const button = event.currentTarget as HTMLButtonElement
  button.parentElement?.querySelectorAll<HTMLButtonElement>('button')[target]?.focus()
}
</script>

<template>
  <div
    class="surface-language"
    :class="{ 'surface-language--panel': surface, 'surface-language--cosmos': surface === 'cosmos' }"
    :role="surface ? 'group' : 'radiogroup'"
    :aria-label="labelledBy ? undefined : label"
    :aria-labelledby="labelledBy"
  >
    <button
      v-for="option in SUPPORTED_LANGUAGES"
      :key="option"
      type="button"
      :role="surface ? undefined : 'radio'"
      :lang="option"
      :aria-label="accessibleLabels[option]"
      :aria-checked="surface ? undefined : language === option"
      :aria-pressed="surface ? language === option : undefined"
      :tabindex="surface || language === option ? 0 : -1"
      @click="setLanguage(option)"
      @keydown="navigate($event, option)"
    >
      {{ labels[option] }}
    </button>
  </div>
</template>

<style scoped>
/* Preserve the separately approved login selector; panels follow the October 9 B-16 row. */
.surface-language { display: inline-flex; align-items: center; gap: 8px; flex: none; }
.surface-language button { height: 24px; min-width: 24px; padding: 0 10px; border: 1px solid var(--fw-border); border-radius: 6px; font-family: inherit; font-size: 11.5px; color: var(--fw-text-secondary); background: transparent; cursor: pointer; }
.surface-language button[aria-checked="true"] { border-color: var(--fw-primary); color: var(--console-chip-fg, var(--fw-primary-light)); background: var(--console-chip-bg, var(--fw-bg-surface)); }
.surface-language button:focus-visible { outline: 2px solid var(--fw-primary-light); outline-offset: 3px; }
.surface-language--cosmos button { border-color: var(--cx-border); color: var(--cx-text-sec); }
.surface-language--cosmos button[aria-checked="true"] { border-color: var(--cx-accent); color: var(--cx-chip-fg); background: var(--cx-chip-bg); }
.surface-language--cosmos button:focus-visible { outline-color: var(--cx-accent-text); }
/* A11Y rule 5: visible control boundaries use the existing AA foreground tokens. */
.surface-language--panel { gap: 6px; }
.surface-language--panel button { height: 28px; padding: 0 11px; border-radius: 7px; font-weight: 400; border-color: var(--fw-text-secondary); }
.surface-language--panel button[aria-pressed="true"] { font-weight: 600; border-color: var(--fw-link); color: var(--fw-link); background: var(--console-chip-bg); }
.surface-language--cosmos button { border-color: var(--cx-text-sec); }
.surface-language--cosmos button[aria-pressed="true"] { border-color: var(--cx-accent-text); color: var(--cx-accent-text); background: var(--cx-chip-bg); }
</style>
